import {
  SelfDescribingJson,
  addGlobalContexts,
  clearGlobalContexts,
  newTracker,
  trackPageView,
  trackSelfDescribingEvent,
} from '@snowplow/browser-tracker'

import config from 'constants/config'
import { APP_CONTEXT_SCHEMA, EXPERIMENTS_CONTEXT_SCHEMA } from 'constants/analytics'
import { getCookies, deleteCookie } from 'lib/web/cookieUtils'
import { overrideTrackerIDs, toExperimentKey } from './helpers'
import { SnowplowEvent, asyncBaseContext, baseContext } from './events'
import { reportError } from 'services/errorReportingService'

const _currentExperiments: { [k: string]: string } = {}

const mergeAsyncContext = (context: SelfDescribingJson<Record<string, unknown>>[] | null | undefined, asyncContext: SelfDescribingJson<Record<string, unknown>>[] | null | undefined) => {
  const newContext = context || []
  newContext.push(...(asyncContext || []))
  return newContext
}

/**
 * Event tracking function
*/
export async function track(event: SnowplowEvent) {
  try {
    const asyncContextResult = await asyncBaseContext()
    event.context = mergeAsyncContext(event?.context, asyncContextResult)
    if ('title' in event) {
      trackPageView(event)
    } else if ('event' in event) {
      trackSelfDescribingEvent(event)
    }
  } catch (error) {
    console.error(error)
  }
}

/**
 * Context setting function
*/
export function upsertContext(type: string, data: {[key: string]: string}) {
  if (type === 'experiments' && 'key' in data && 'value' in data) {
    _currentExperiments[toExperimentKey(data.key)] = data.value
  }
  else throw new Error('Someone is not using this function correctly')
}

export function resetContext() {
  clearGlobalContexts()

  addGlobalContexts([{
    schema: APP_CONTEXT_SCHEMA,
    data: {
      appVersion: config.commitSha || '-',
    },
  },
  baseContext,
  () => {
    return {
      schema: EXPERIMENTS_CONTEXT_SCHEMA,
      data: {
        flags: Object.entries(_currentExperiments).map(([key, value]) => ({
          key,
          value: `${value}`,
        })),
      },
    }
  }])
}

/**
 * Init function
*/
let listenerAdded = false
export function init() {
  const tracker = newTracker('sp', config.TRACKING_HOST, {
    // MOBILE_APP_PLATFORM value (ios/android) will be passed only for the WL-Mobile build
    appId: config.MOBILE_APP_PLATFORM ? config.wlMobileApp[config.MOBILE_APP_PLATFORM].snowPlow.appId : config.snowPlow.appId,
    plugins: [],
    // you must specify the path since the default path
    // /com.snowplowanalytics.snowplow/tp2 will be blocked by adblock
    postPath: '/com.luxgroup/tp2',
    // not-encoding the data makes debugging easier
    encodeBase64: false,
  })

  try {
    const domainUserIdKey = 'lux_sp_domain_user_id'
    const sessionIdKey = 'lux_sp_session_id'
    const sessionIdxKey = 'lux_sp_session_idx'
    const dict = getCookies()
    // If a user came from mobile webview,
    // we will override the domain ID, session ID and session IDX with
    // values that come from the mobile webview.
    if (dict[domainUserIdKey] || dict[sessionIdKey] || dict[sessionIdxKey]) {
      try {
        overrideTrackerIDs(
          tracker,
          dict[sessionIdKey],
          dict[domainUserIdKey],
          Number.isFinite(dict[sessionIdxKey]) ? Number(dict[sessionIdxKey]) : undefined,
        )
      } catch (e) {
        reportError(e)
      }

      deleteCookie(domainUserIdKey)
      deleteCookie(sessionIdxKey)
      deleteCookie(sessionIdKey)
    }
  } catch {
    console.error('Snowplow init error')
  }

  if (!listenerAdded) {
    listenerAdded = true
    resetContext()
  }
}
